import { useState, useEffect } from 'react';
import { WifiOff, Wifi } from 'lucide-react';
import { cn } from '@/lib/utils';
import { offlineDB } from '@/lib/offline/offlineDB';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { useOnlineStatus } from '@/hooks/offline/useOnlineStatus';


export const OfflineIndicator = () => {
  // Estado de conexión centralizado
  const isOnline = useOnlineStatus();
  const [pendingCount, setPendingCount] = useState(0);

  // Cargar contador de pendientes
  const loadPendingCount = async () => {
    const count = await offlineDB.countPending();
    setPendingCount(count);
  };

  useEffect(() => {
    loadPendingCount();

    const handleUpdate = () => loadPendingCount();
    window.addEventListener('offline-logs-updated', handleUpdate);

    return () => window.removeEventListener('offline-logs-updated', handleUpdate);
  }, []);

  // Si todo está bien (online y sin pendientes), mostrar verde discreto
  if (isOnline && pendingCount === 0) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center justify-center w-9 h-9 rounded-md bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400 transition-all cursor-default">
              <Wifi className="h-4 w-4" />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">Conexión estable</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Si está offline o hay pendientes
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn(
            "flex items-center justify-center gap-1.5 px-2.5 h-9 rounded-md transition-all cursor-default relative",
            isOnline 
              ? "bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-400" 
              : "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400 animate-pulse"
          )}>
            {isOnline ? (
              <>
                <Wifi className="h-4 w-4" />
                {pendingCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-orange-500 text-white text-[10px] font-bold">
                    {pendingCount}
                  </span>
                )}
              </>
            ) : (
              <WifiOff className="h-4 w-4" />
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">
            {isOnline 
              ? `${pendingCount} pendiente(s) de sincronizar` 
              : 'Sin conexión a internet'}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};